import { deleteRequest, getRequest, postRequest, putRequest, patchRequest } from "../../utils/apiHelper";
import type { Opportunity } from "../../containers/Pipelines/types";

// Backend response format (what apiHelper.handleResponse returns)
interface BackendResponse {
  http_code: number;
  status: string; // "success" or "failed"
  message: string;
  data: any;
}

// Our service response format
interface ApiResponse<T> {
  success?: boolean;
  status?: string;
  message?: string;
  data?: T;
  http_code?: number;
}

interface OpportunitiesResponse {
  opportunities: Opportunity[];
}

interface OpportunityResponse {
  opportunity: Opportunity;
}

interface CreateOpportunityRequest {
  stage_id: number;
  lead_id: number;
  value: number;
  expected_close_date: string;
}

interface UpdateOpportunityRequest {
  lead_id?: number;
  value?: number;
  expected_close_date?: string;
  stage_id?: number;
}

interface UpdateOpportunityOrderRequest {
  order_index: number;
  stage_id?: number;
}

interface OpportunityFilters {
  search?: string;
  leadIds?: number[];
  stageIds?: number[];
  stageTags?: string[];
  valueMin?: number;
  valueMax?: number;
  expectedDateFrom?: string;
  expectedDateTo?: string;
}

// Get opportunities by pipeline with optional filters
const getOpportunitiesByPipeline = async (
  pipelineId: number, 
  filters?: OpportunityFilters
): Promise<ApiResponse<OpportunitiesResponse>> => {
  let url = `pipelines/${pipelineId}/opportunities`;
  
  // Build query params if filters provided
  if (filters) {
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.leadIds?.length) params.append('leadIds', filters.leadIds.join(','));
    if (filters.stageIds?.length) params.append('stageIds', filters.stageIds.join(','));
    if (filters.stageTags?.length) params.append('stageTags', filters.stageTags.join(','));
    if (filters.valueMin !== undefined) params.append('valueMin', filters.valueMin.toString());
    if (filters.valueMax !== undefined) params.append('valueMax', filters.valueMax.toString());
    if (filters.expectedDateFrom) params.append('expectedDateFrom', filters.expectedDateFrom);
    if (filters.expectedDateTo) params.append('expectedDateTo', filters.expectedDateTo);
    
    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }
  
  // apiHelper returns BackendResponse but TypeScript thinks it's AxiosResponse
  const rawResponse = await getRequest(url) as unknown as BackendResponse;
  
  // Transform backend response to our ApiResponse format
  return {
    success: rawResponse.status === 'success',
    status: rawResponse.status,
    message: rawResponse.message,
    data: rawResponse.data as OpportunitiesResponse,
    http_code: rawResponse.http_code
  };
};

// Get opportunities by stage
const getOpportunitiesByStage = async (stageId: number): Promise<ApiResponse<OpportunitiesResponse>> => {
  const rawResponse = await getRequest(`stages/${stageId}/opportunities`) as unknown as BackendResponse;
  
  return {
    success: rawResponse.status === 'success',
    status: rawResponse.status,
    message: rawResponse.message,
    data: rawResponse.data as OpportunitiesResponse,
    http_code: rawResponse.http_code
  };
};

// Get single opportunity
const getOpportunity = async (id: number): Promise<ApiResponse<OpportunityResponse>> => {
  const rawResponse = await getRequest(`opportunities/${id}`) as unknown as BackendResponse;
  
  return {
    success: rawResponse.status === 'success',
    status: rawResponse.status,
    message: rawResponse.message,
    data: rawResponse.data as OpportunityResponse,
    http_code: rawResponse.http_code
  };
};

// Create new opportunity
const createOpportunity = async (params: CreateOpportunityRequest): Promise<ApiResponse<OpportunityResponse>> => {
  // @ts-ignore - API helper typing issue with multiple parameters
  const rawResponse = await postRequest("opportunities", params, false, undefined, "default", undefined) as unknown as BackendResponse;
  
  return {
    success: rawResponse.status === 'success',
    status: rawResponse.status,
    message: rawResponse.message,
    data: rawResponse.data as OpportunityResponse,
    http_code: rawResponse.http_code
  };
};

// Update opportunity
const updateOpportunity = async (id: number, params: UpdateOpportunityRequest): Promise<ApiResponse<OpportunityResponse>> => {
  // @ts-ignore - API helper typing issue with multiple parameters
  const rawResponse = await putRequest(`opportunities/${id}`, params, false, "default") as unknown as BackendResponse;
  
  return {
    success: rawResponse.status === 'success',
    status: rawResponse.status,
    message: rawResponse.message,
    data: rawResponse.data as OpportunityResponse,
    http_code: rawResponse.http_code
  };
};

// Delete opportunity
const deleteOpportunity = async (id: number): Promise<ApiResponse<{ id: number }>> => {
  // @ts-ignore - API helper typing issue with multiple parameters
  const rawResponse = await deleteRequest(`opportunities/${id}`, undefined, "default") as unknown as BackendResponse;
  
  return {
    success: rawResponse.status === 'success',
    status: rawResponse.status,
    message: rawResponse.message,
    data: rawResponse.data as { id: number },
    http_code: rawResponse.http_code
  };
};

// Update opportunity order (for drag & drop)
const updateOpportunityOrder = async (id: number, params: UpdateOpportunityOrderRequest): Promise<ApiResponse<OpportunityResponse>> => {
  // @ts-ignore - API helper typing issue with multiple parameters
  const rawResponse = await patchRequest(`opportunities/${id}/order`, params, false, undefined, "default") as unknown as BackendResponse;
  
  return {
    success: rawResponse.status === 'success',
    status: rawResponse.status,
    message: rawResponse.message,
    data: rawResponse.data as OpportunityResponse,
    http_code: rawResponse.http_code
  };
};

interface BulkCreateOpportunitiesRequest {
  lead_ids: number[];
  stage_id: number;
}

interface BulkCreateOpportunitiesResponse {
  added: number;
  skipped: { already_in_pipeline: number; not_found: number };
  stage_name: string;
  pipeline_name: string;
}

// Add a batch of leads to a pipeline stage. Leads already on the pipeline are skipped.
const bulkCreateOpportunities = async (
  params: BulkCreateOpportunitiesRequest
): Promise<BulkCreateOpportunitiesResponse> => {
  const rawResponse = await postRequest(
    'opportunities/bulk',
    params,
    false,
    undefined,
    'default',
    undefined
  ) as unknown as BackendResponse;
  return rawResponse.data as BulkCreateOpportunitiesResponse;
};

export const opportunityService = {
  bulkCreateOpportunities,
  getOpportunitiesByPipeline,
  getOpportunitiesByStage,
  getOpportunity,
  createOpportunity,
  updateOpportunity,
  deleteOpportunity,
  updateOpportunityOrder,
};

export type {
  BulkCreateOpportunitiesRequest,
  BulkCreateOpportunitiesResponse,
  CreateOpportunityRequest,
  UpdateOpportunityRequest,
  UpdateOpportunityOrderRequest,
  OpportunityFilters,
};
